// App shell (Plan 7, Main mockup): the nav rail — brand, the titular filter, Atividades (with the count of what asks for
// action), Processos, Território, Carteira (consultorias), and at the bottom the sources' status (a dot for the worst
// state, linking to /status) — pinned to the viewport while the main column scrolls. The main column: a header with
// the Consulta search and the account menu, then the screen under the MFA banner (S-x) while the login has no second
// factor.
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnmEntities, useHoje, useStatus, useTitular } from '../lib/queries.ts';
import { useSession } from '../lib/session.ts';
import { DOT, TONE } from '../lib/status.ts';
import { formatDateTime } from '../lib/time.ts';
import { AccountMenu } from './AccountMenu.tsx';
import { AnmEntityFilter } from './AnmEntityFilter.tsx';
import { MfaBanner } from './MfaBanner.tsx';
import lockup from '../assets/lockup.svg';

const NAV = [
  { to: '/', key: 'atividades', consultant: false },
  // S-z: a consultoria's Carteira leads right after Atividades
  { to: '/carteira', key: 'carteira', consultant: true },
  { to: '/processos', key: 'processos', consultant: false },
  { to: '/territorio', key: 'territorio', consultant: false },
] as const;

const LINK =
  'flex items-center justify-between gap-2 rounded-button px-3 py-2 text-sm text-body no-underline hover:bg-line-2 data-[status=active]:bg-wash data-[status=active]:font-semibold data-[status=active]:text-deep';

/** Atividades' badge: the act-now obligations of /hoje not yet resolved (the same query the screen reads — no extra cost there). */
function ActNowBadge() {
  const { t } = useTranslation();
  const n = useHoje(useTitular()).data?.actNow.filter((o) => !o.state?.resolvedAt).length ?? 0;
  if (!n) return null;
  return (
    <span className="rounded-pill bg-crit px-1.5 py-px text-xs font-semibold text-white tabular">
      <span aria-hidden="true">{n}</span>
      <span className="sr-only">{t('shell.actNow', { count: n })}</span>
    </span>
  );
}

/** The bottom of the rail: "Fontes em dia · 28/10 · 10:15", the dot coloured by the worst source state. */
function SourcesStatus() {
  const { t } = useTranslation();
  const status = useStatus().data;
  const summary = status ? `${t(`shell.status.${TONE[status.state]}`)} · ${formatDateTime(status.checkedAt)}` : null;
  return (
    <Link
      to="/status"
      search={(prev) => ({ titular: prev.titular })}
      aria-label={summary ? t('shell.status.aria', { summary }) : undefined}
      className="mt-auto flex items-center gap-2 rounded-button px-3 py-2 text-xs text-body no-underline hover:bg-line-2 data-[status=active]:bg-wash data-[status=active]:text-deep"
    >
      {status && (
        <span
          role="img"
          aria-label={t('nav.statusDot', { state: t(`status.states.${status.state}`) })}
          className={`size-2 shrink-0 rounded-full ${DOT[TONE[status.state]]}`}
        />
      )}
      <span>{summary ?? t('shell.status.link')}</span>
    </Link>
  );
}

/** Consulta from anywhere: Enter goes to /consulta?q=<text>. */
function HeaderSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <form
      role="search"
      aria-label={t('shell.search.region')}
      onSubmit={(e) => {
        e.preventDefault();
        const text = q.trim().slice(0, 120);
        if (text) void navigate({ to: '/consulta', search: { q: text } });
      }}
      className="flex min-w-0 flex-[1_1_320px] items-center gap-2 rounded-button border border-line bg-canvas px-3 focus-within:ring-2 focus-within:ring-brand"
    >
      <label htmlFor="header-search" className="sr-only">
        {t('shell.search.label')}
      </label>
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-faint">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        id="header-search"
        type="search"
        value={q}
        maxLength={120}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('shell.search.placeholder')}
        className="min-h-10 min-w-0 flex-1 bg-transparent text-sm text-ink outline-none"
      />
      <button type="submit" className="sr-only">
        {t('shell.search.submit')}
      </button>
    </form>
  );
}

export function Shell() {
  const { t } = useTranslation();
  const session = useSession();
  const entities = useAnmEntities();
  const onSecurity = useRouterState({ select: (r) => r.location.pathname === '/conta/seguranca' });
  const customer = session.status === 'signed_in' ? session.me.customers.find((c) => c.id === session.customerId) : undefined;
  // the Carteira is a consultoria's screen
  const nav = NAV.filter((n) => !n.consultant || customer?.isConsultant);
  return (
    <div className="flex min-h-screen">
      <nav aria-label="Principal" className="sticky top-0 flex h-screen w-64 shrink-0 flex-col gap-6 overflow-y-auto border-r border-line bg-card px-4 py-5">
        <div>
          <img src={lockup} alt={t('brand.name')} width={176} height={29} className="h-[29px] w-auto" />
        </div>
        {session.status === 'signed_in' && <AnmEntityFilter entities={entities} />}
        <ul className="flex flex-col gap-0.5">
          {nav.map((n) => (
            <li key={n.to}>
              <Link to={n.to} search={(prev) => ({ titular: prev.titular })} activeOptions={{ exact: n.to === '/', includeSearch: false }} className={LINK}>
                {t(`shell.nav.${n.key}`)}
                {n.key === 'atividades' && session.status === 'signed_in' && <ActNowBadge />}
              </Link>
            </li>
          ))}
        </ul>
        <SourcesStatus />
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-3 border-b border-line bg-card px-8 py-3">
          <HeaderSearch />
          <AccountMenu />
        </header>
        <main className="min-w-0 flex-1 px-8 py-6">
          {!onSecurity && <MfaBanner />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
